import '../assets/cryptojs';
import React, { useCallback, useEffect, useState } from 'react';
import { initDB } from 'react-indexed-db-hook';
import App from './App';
import SessionContext from './contexts/SessionContext';
import useSession from './hooks/useSession';
import useDerivative from './hooks/useDerivative';
import addMatomoScript from './logic/matomo';
import styleOverride from './logic/styleOverride';
import faviconOverride from './logic/faviconOverride';
import { getPreviewClassName, isPreview } from './logic/helpers';
import { LanguageProvider } from './contexts/LanguageContext';
import Loader from './components/Loader';
import { loadGoogleScript } from './hooks/useGoogleMaps';
import { AppProvider } from './contexts/AppContext';
import { DerivateSettings, PreviewDerivate } from './api/derivate';
import PwaInstallBanner from './components/PwaInstallBanner';

/** Setup Local DB für caching profile images */
initDB({
  name: 'PendlerPortal',
  version: 1,
  objectStoresMeta: [
    {
      store: 'images',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'guid', keypath: 'guid', options: { unique: true } },
      ],
    },
  ],
});

// eslint-disable-next-line react/prop-types
const Bootstrap = () => {
  const { setToken, userdata, setUserData, clearSession } = useSession();
  const derivative = useDerivative();

  const [dbReady, setDbReady] = useState(false);
  const [derivativeReady, setDerivativeReady] = useState(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [sessionData, setSessionData] = useState(userdata);
  const setSessionDataAndStore = (sd) => {
    if (sd) {
      setUserData(sd);
      setToken(sd?.token);
    } else {
      clearSession();
    }
    setSessionData(sd);
  };

  const setHeadContent = (selector, content, attribute = 'content') => {
    if (!content) return;
    const node = document.querySelector(selector);
    if (node) {
      if (attribute === 'textContent') {
        node.textContent = content;
      } else {
        node.setAttribute(attribute, content);
      }
    }
  };

  const applyDerivativeSeo = useCallback((d) => {
    const displayName = d.displayName || d.name || 'Pendlerportal';
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const description =
      d.description ||
      d.metaDescription ||
      descriptionMeta?.getAttribute('content');
    const origin = window.location.origin;
    const canonicalUrl = `${origin}/`;
    const socialImage = `${origin}/applogo_512x512.png`;
    const languages = d.languages?.length ? d.languages : ['de'];

    document.title = displayName;
    document.documentElement.lang = languages[0];

    setHeadContent('meta[name="application-name"]', displayName);
    setHeadContent('meta[name="apple-mobile-web-app-title"]', displayName);
    setHeadContent('meta[name="description"]', description);
    setHeadContent('meta[property="og:description"]', description);
    setHeadContent('meta[name="twitter:description"]', description);
    setHeadContent('#meta-og-title', displayName);
    setHeadContent('#meta-og-site-name', displayName);
    setHeadContent('#meta-twitter-title', displayName);
    setHeadContent('#meta-og-url', canonicalUrl);
    setHeadContent('#meta-canonical', canonicalUrl, 'href');
    setHeadContent('#meta-hreflang-de', canonicalUrl, 'href');
    setHeadContent('#meta-hreflang-default', canonicalUrl, 'href');
    setHeadContent('#meta-og-image', socialImage);
    setHeadContent('#meta-twitter-image', socialImage);

    const orgSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: displayName,
      url: canonicalUrl,
      logo: socialImage,
      sameAs: [],
    };
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: displayName,
      url: canonicalUrl,
      inLanguage: languages,
      description,
    };
    const webAppSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: displayName,
      url: canonicalUrl,
      applicationCategory: 'TravelApplication',
      operatingSystem: 'Web',
      inLanguage: languages,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
      },
    };

    setHeadContent(
      '#schema-organization',
      JSON.stringify(orgSchema),
      'textContent',
    );
    setHeadContent(
      '#schema-website',
      JSON.stringify(websiteSchema),
      'textContent',
    );
    setHeadContent(
      '#schema-webapp',
      JSON.stringify(webAppSchema),
      'textContent',
    );
  }, []);

  const initDerivative = useCallback(
    async (d) => {
      if (d.matomoSiteId && process.env.REACT_APP_ENV !== 'development') {
        addMatomoScript(d.matomoSiteId);
      }

      if (d.className) {
        /* Set class name. This is used by the CSS variables */
        document.querySelector('html').classList.add(d.className);
        /* Set title and metadata for the current white-label derivative */
        applyDerivativeSeo(d);
        /* Override website favicon */
        faviconOverride(d.className);
        /* Override css variables if available in cdn folder */
        styleOverride(d.className);
      }
      setDerivativeReady(d);
    },
    [applyDerivativeSeo],
  );

  useEffect(() => {
    setDbReady(true);
    loadGoogleScript('de', setGoogleReady);
    /** Setup Derivatives */
    const preview = isPreview();
    if (preview) {
      const className = getPreviewClassName();
      PreviewDerivate(className).then((result) => {
        if (result) {
          localStorage.setItem('derivative', JSON.stringify(result));
          initDerivative(result);
        } else {
          initDerivative(derivative);
        }
      });
    } else if (process.env.REACT_APP_ENV === 'development') {
      initDerivative(derivative);
    } else {
      DerivateSettings().then((result) => {
        if (result) {
          const matchUrls = (url) => url === window.location.host;
          if (!result.urls.some(matchUrls)) {
            window.location.replace('https://pendlerportal.de'); // redirect if subdomain does not exist.
          } else {
            localStorage.setItem('derivative', JSON.stringify(result));
            initDerivative(result);
          }
        }
      });
    }
  }, []);

  if (!dbReady || !derivativeReady || !googleReady) {
    return (
      <>
        <Loader />
      </>
    );
  }

  return (
    <SessionContext.Provider
      value={{
        sessionData,
        setSessionData: setSessionDataAndStore,
      }}
    >
      <LanguageProvider
        className={derivativeReady.className}
        languages={derivativeReady.languages || ['de']}
        loadCustomTexts
      >
        <AppProvider>
          <PwaInstallBanner />
          <App />
        </AppProvider>
      </LanguageProvider>
    </SessionContext.Provider>
  );
};

export default Bootstrap;
